// HTTP Cloud Functions used by the mobile app (same project, same contracts).
// Every call sends the user's Firebase ID token: the server uses it to check
// who is calling and to enforce AI allowances.
import { auth } from '../../firebase';
import { unitStyle } from './unitPreference';

const BASE = 'https://us-central1-fitviz-4cc89.cloudfunctions.net';
const PROGRAM_URL = 'https://get-program-bf3pwmjvca-uc.a.run.app';

// Language the AI answers in. LanguageContext keeps <html lang> on the
// current UI language.
const appLanguage = () => (document.documentElement.lang === 'tr' ? 'tr' : 'en');

async function post(url, data) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ data }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function stripFences(raw) {
  const s = String(raw).trim();
  if (!s.startsWith('```')) return s;
  return s.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '').trim();
}

function asObject(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') return JSON.parse(stripFences(raw));
  return raw;
}

const toInt = (v) => Math.round(Number(v) || 0);

function parseEstimate(json) {
  const n = asObject(json?.nutrition_estimate);
  if (!n) return null;
  const items = (n.items || []).map((i) => ({
    foodName: i.food_name || '',
    quantity: i.quantity || '',
    // quantity as a number in unit ("1" + "tabak"/"plate", "150" + "g"): the review
    // modal scales the macros when it is edited. Null on older estimates.
    amount: Number(i.amount) > 0 ? Number(i.amount) : null,
    unit: i.unit ? String(i.unit).trim().toLowerCase() : null,
    quantityEstimated: !!i.quantity_estimated,
    caloriesKcal: toInt(i.calories_kcal),
    proteinG: toInt(i.protein_g),
    carbsG: toInt(i.carbs_g),
    fatG: toInt(i.fat_g),
    assumptionNote: i.assumption_note || null,
  }));
  const tot = n.meal_totals || {};
  return {
    mealType: n.meal_type || '',
    items,
    mealTotals: {
      caloriesKcal: toInt(tot.calories_kcal),
      proteinG: toInt(tot.protein_g),
      carbsG: toInt(tot.carbs_g),
      fatG: toInt(tot.fat_g),
    },
    // Photo estimates only: pays for one addPhotoMealItems call.
    addItemToken: typeof json?.add_item_token === 'string' ? json.add_item_token : null,
  };
}

export async function getNutritionEstimate(mealType, userInput) {
  return parseEstimate(
    await post(`${BASE}/get_nutrition_estimate`, { meal_type: mealType, user_input: userInput, unit_style: unitStyle(), lang: appLanguage() }),
  );
}

export async function getNutritionEstimateFromPhoto(mealType, imageBase64, mimeType) {
  return parseEstimate(
    await post(`${BASE}/get_nutrition_estimate_from_photo`, {
      meal_type: mealType,
      image_base64: imageBase64,
      mime_type: mimeType,
      unit_style: unitStyle(),
      lang: appLanguage(),
    }),
  );
}

export const MAX_ADD_ITEMS = 5;
export const MAX_ADD_ITEM_CHARS = 120;

/**
 * One text estimate of all the foods a photo estimate missed, paid by that
 * photo's addItemToken (once per photo) instead of a nutrition use.
 * Returns {estimate, grantUsed}; estimate is null on failure.
 */
export async function addPhotoMealItems(mealType, foods, addItemToken) {
  try {
    const estimate = parseEstimate(
      await post(`${BASE}/add_photo_meal_item`, {
        meal_type: mealType,
        user_inputs: foods,
        add_item_token: addItemToken,
        unit_style: unitStyle(),
        lang: appLanguage(),
      }),
    );
    return { estimate, grantUsed: false };
  } catch (e) {
    return { estimate: null, grantUsed: e.message === 'HTTP 429' };
  }
}

export async function getMacroTargets(profile) {
  const body = await post(`${BASE}/get_macro_targets`, { profile });
  const m = asObject(body?.macro_targets);
  if (!m) return null;
  const t = {
    protein_g: toInt(m.protein_g),
    carbs_g: toInt(m.carbs_g),
    fat_g: toInt(m.fat_g),
    daily_calories: toInt(m.daily_calories),
  };
  if (t.daily_calories <= 0 || t.protein_g <= 0 || t.carbs_g <= 0 || t.fat_g <= 0) return null;
  return t;
}

export async function analyzeProgram(userUid) {
  let body = await post(`${BASE}/analyze_program`, { user_uid: userUid, lang: appLanguage() });
  if (typeof body === 'string') body = JSON.parse(body);
  const list = (v) => (Array.isArray(v) ? v.map((x) => String(x ?? '')).filter(Boolean) : []);
  return { pros: list(body.pros), cons: list(body.cons), advices: list(body.advices) };
}

export async function getWeeklySummary(userUid, weekStart, language) {
  const m = await post(`${BASE}/get_weekly_summary`, { user_uid: userUid, week_start: weekStart, language });
  return {
    weekStart: m.week_start || '',
    weekEnd: m.week_end || '',
    language: m.language || language,
    headline: m.headline || '',
    programSummary: m.program_summary || '',
    nutritionSummary: m.nutrition_summary || '',
    tips: Array.isArray(m.tips) ? m.tips.map(String) : [],
    stats: m.stats && typeof m.stats === 'object' ? m.stats : {},
  };
}

/**
 * get-program (v2): sends every AI Trainer choice and gets
 * {program: {day1: [{exerciseId, sets, reps, duration_min}]}, titles: {day1: '...'}}.
 * Day slots map onto the selected weekdays in week order — same as
 * GptProgramService.generateProgram in the app. Returns {program, titles}.
 */
export async function getProgramSuggestion({
  level, goal, programType, days, equipment, sessionMinutes, focus, limitations,
  includeCardio, note, lang, exerciseById,
}) {
  const body = await post(PROGRAM_URL, {
    v: 2,
    level,
    goal,
    program_type: programType,
    n_days: days.length,
    equipment,
    session_minutes: sessionMinutes,
    focus,
    limitations,
    include_cardio: includeCardio,
    ...(note && note.trim() ? { note: note.trim() } : {}),
    lang,
  });
  const programData = body?.program;
  if (!programData) return null;
  const titles = body?.titles || {};
  const order = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const sortedDays = [...days].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const slots = Object.keys(programData)
    .filter((k) => (parseInt(String(k).replace('day', ''), 10) || 0) > 0)
    .sort((a, b) => parseInt(a.replace('day', ''), 10) - parseInt(b.replace('day', ''), 10));
  const program = {};
  const dayTitles = {};
  slots.slice(0, sortedDays.length).forEach((slot, i) => {
    const list = programData[slot];
    if (!Array.isArray(list)) return;
    const day = sortedDays[i];
    program[day] = list
      .map((raw) => {
        // Older servers return bare ids.
        const item = raw && typeof raw === 'object' ? raw : { exerciseId: String(raw) };
        const exId = item.exerciseId ? String(item.exerciseId) : '';
        if (!exId) return null;
        const ex = exerciseById[exId];
        const cardio = ex && String(ex.muscle).trim().toLowerCase() === 'cardio';
        return cardio
          ? { exerciseId: exId, sets: 1, reps: 1, durationMin: toInt(item.duration_min) || 20, weight: null }
          : { exerciseId: exId, sets: toInt(item.sets) || 3, reps: toInt(item.reps) || 12, durationMin: null, weight: null };
      })
      .filter(Boolean);
    if (typeof titles[slot] === 'string' && titles[slot].trim()) dayTitles[day] = titles[slot].trim();
  });
  return { program, titles: dayTitles };
}

/** Redeems a promo code on the website. Returns {status, expiresAt?}. */
export async function redeemPromoCode(code) {
  return post(`${BASE}/redeem_promo_code`, { code, platform: 'web' });
}

/**
 * Pays the XP earned today and yesterday (fitviz_cloud xp.py). Returns
 * {xp, level, founder, gains: [{source, xp}], levelUps: [{level, prize}]}.
 */
export async function syncXp() {
  return post(`${BASE}/sync_xp`, {});
}

/** Re-reads the caller's plan from RevenueCat (e.g. drops an expired promo). */
export async function syncSubscription() {
  return post(`${BASE}/sync_subscription`, {});
}

/** Admin panel API (admin custom claim required). Returns the JSON body, errors included. */
async function adminCall(endpoint, action, payload) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${BASE}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ data: { action, ...payload } }),
  });
  let body = {};
  try {
    body = await res.json();
  } catch (e) {
    // empty / non-JSON body
  }
  if (!res.ok && !body.error) body.error = `HTTP ${res.status}`;
  return body;
}

export function adminPromoCodes(action, payload = {}) {
  return adminCall('admin_promo_codes', action, payload);
}

export function adminFeedback(action, payload = {}) {
  return adminCall('admin_feedback_api', action, payload);
}

/** Top [limit] users by XP. */
export function adminXpLeaderboard(limit) {
  return adminCall('admin_xp_leaderboard', 'list', { limit });
}

/** Trainers with at least one active trainee, each with their trainees. */
export function adminTrainers() {
  return adminCall('admin_trainers_api', 'list', {});
}
